'use client';

import React, { useEffect, useState } from 'react';
import { Clock, Tag } from 'lucide-react';
import { getChangelog, type ChangelogEntry } from '@/lib/api-client';
import { useTimezone } from '@/hooks/useTimezone';

function formatDateWithTz(dateStr: string, tz: string) {
  const ymd = dateStr ? dateStr.slice(0, 10) : '';
  if (!ymd) return '—';
  const d = new Date(ymd + 'T12:00:00');
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: tz });
}

export default function ChangelogPage() {
  const tz = useTimezone();
  const [entries, setEntries] = useState<ChangelogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getChangelog()
      .then(setEntries)
      .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Failed to load changelog'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-surface text-white">
      {/* Header */}
      <div className="border-b border-card-bg">
        <div className="max-w-3xl mx-auto px-3 py-12">
          <div className="inline-flex items-center gap-2 bg-sky-500/10 border border-sky-500/20 rounded-full px-4 py-1.5 text-sky-400 text-sm font-medium mb-5">
            <Clock size={14} />
            Release History
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Changelog</h1>
          <p className="text-fg-muted">
            A record of new features, improvements, and fixes shipped to StocksIQPro.
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-3 py-12">
        {loading && (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-6 text-red-400 text-sm text-center">
            {error}
          </div>
        )}

        {!loading && !error && entries.length === 0 && (
          <div className="text-center text-fg-subtle py-16">No changelog entries yet.</div>
        )}

        {!loading && !error && entries.length > 0 && (
          <div className="relative">
            {/* Vertical timeline line */}
            <div className="absolute left-4 top-2 bottom-2 w-px bg-border-default" />

            <div className="space-y-10">
              {entries.map((entry) => (
                <div key={entry.id} className="relative pl-12">
                  {/* Timeline dot */}
                  <div className="absolute left-0 top-1.5 w-8 h-8 rounded-full bg-card-bg border-2 border-sky-500 flex items-center justify-center">
                    <div className="w-2.5 h-2.5 rounded-full bg-sky-400" />
                  </div>

                  <div className="bg-card-bg border border-default rounded-xl p-6">
                    <div className="flex flex-wrap items-center gap-3 mb-3">
                      {entry.version && (
                        <span className="inline-flex items-center gap-1.5 bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-bold px-2.5 py-1 rounded-full">
                          <Tag size={11} />
                          v{entry.version}
                        </span>
                      )}
                      <span className="text-fg-subtle text-xs flex items-center gap-1">
                        <Clock size={11} />
                        {formatDateWithTz(entry.releasedAt, tz)}
                      </span>
                    </div>
                    <h2 className="text-lg font-bold text-white mb-2">{entry.title}</h2>
                    {entry.description && (
                      <p className="text-fg-muted text-sm leading-relaxed whitespace-pre-wrap">
                        {entry.description}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
